import { Bell, Headset, MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client';
import type { AssistAcceptPayload, AssistPayload, AssistSession } from '../api/types';
import { AssistStage } from '../components/AssistStage';
import { type ActiveCall, useAssistCall } from '../lib/assistCall';
import { duration } from '../lib/format';
import { can, useMe } from '../lib/me';
import {
  assistNotificationState,
  requestAssistNotifications,
} from '../lib/useAssistPending';
import { useLoad } from '../lib/useLoad';

/**
 * Guided support sessions: who is waiting, and the screen of whoever you took.
 *
 * WHY THIS POLLS RATHER THAN SUBSCRIBING
 *
 * Same reasoning as the operator's side (routes/assist.js): a request sits for
 * as long as it takes somebody to look, and four seconds of staleness on a queue
 * that fills a few times a day is invisible. A websocket would add an auth
 * handshake and a reconnect story to this console for no perceptible gain.
 *
 * WHAT IS SHOWN BEFORE ACCEPTING, AND WHY IT MATTERS
 *
 * The route, the app version and the browser come with the request. A fair share
 * of them are answerable from those three alone -- "you are on the old build",
 * "that screen needs the permission you do not have" -- and answering without
 * joining is strictly better for everyone. So the context is on the card, not
 * behind a click.
 */

const POLL_MS = 4000;

/**
 * A session that has begun costs money and shows somebody's screen, so `live`
 * gets the loudest colour on the page rather than a neutral one.
 *
 * Closed states map to '' -- the base `.badge` is already the muted style. NOT
 * `badge-dot`, which is a 6px circle meant to sit INSIDE a badge; used as a
 * modifier it collapses the badge to 6px and crushes the label.
 */
const STATUS_TONE: Record<string, string> = {
  pending: 'badge-warn',
  accepted: 'badge-accent',
  live: 'badge-danger',
  ended: '',
  expired: '',
  declined: '',
};

function age(iso: string | null): string {
  if (!iso) return '—';
  return `${duration(Date.now() - new Date(iso).getTime())} ago`;
}

function ContextRow({ session }: { session: AssistSession }) {
  const entries = Object.entries(session.context || {});
  if (!entries.length) return null;
  return (
    <dl className="assist-context">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Assist() {
  const { data, error, loading, reload } = useLoad<AssistPayload>(
    () => api.get<AssistPayload>('/assist'),
    [],
    POLL_MS,
  );
  const me = useMe();
  const canRespond = can(me, 'assist.respond');

  // The console holds the call (lib/assistCall.tsx) so it survives leaving this
  // screen. Without a provider -- the fixture preview -- this screen holds it
  // itself, as it always did.
  const shared = useAssistCall();
  const [localCall, setLocalCall] = useState<ActiveCall | null>(null);
  const joined = shared ? shared.call : localCall;
  const start = (call: ActiveCall) => (shared ? shared.start(call) : setLocalCall(call));
  const [busy, setBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notifyState, setNotifyState] = useState(assistNotificationState);

  const accept = async (session: AssistSession) => {
    setBusy(session.id);
    setActionError(null);
    try {
      const result = await api.post<AssistAcceptPayload>(`/assist/${session.id}/accept`);
      start({ session, roomUrl: result.roomUrl, token: result.token });
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Back into a session this person accepted -- after a reload, or another
   * tab -- with a fresh token for the same room. Only whoever accepted it, or
   * root: the server refuses anyone else (routes/index.js, /assist/:id/token),
   * and the button is not drawn for them.
   */
  const rejoin = async (session: AssistSession) => {
    setBusy(session.id);
    setActionError(null);
    try {
      const result = await api.post<AssistAcceptPayload>(`/assist/${session.id}/token`);
      start({ session, roomUrl: result.roomUrl, token: result.token });
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
      void reload();
    } finally {
      setBusy(null);
    }
  };

  /** End a session for everyone -- the one on screen, or one left open elsewhere. */
  const end = async (session: AssistSession) => {
    setActionError(null);
    if (joined?.session.id === session.id) {
      if (shared) {
        await shared.finish();
        void reload();
        return;
      }
      // The stage is torn down FIRST. Ending the session deletes the room, and
      // a call object still joined to a deleted room surfaces as an error
      // dialog on top of a console that is otherwise behaving correctly.
      setLocalCall(null);
    }
    setBusy(session.id);
    try {
      await api.post(`/assist/${session.id}/end`);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
    void reload();
  };

  if (joined) {
    // The console draws a call it holds (components/AssistCallHost.tsx).
    if (shared) return null;
    // Not inside `.page`: that is a 980px reading column, and it drew a
    // 1920px-wide shared screen at under half size. The stage takes the whole
    // window below the nav instead -- `.wt-shell:has(.assist-stage)` in ui.css.
    return (
      <AssistStage
        roomUrl={joined.roomUrl}
        token={joined.token}
        tenantKey={joined.session.tenantKey}
        onEnded={() => void end(joined.session)}
      />
    );
  }

  const mine = (session: AssistSession) =>
    Boolean(me && (me.isRoot || (session.acceptedBy ?? '').toLowerCase() === me.email.toLowerCase()));

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">
          <Headset size={18} /> Assist
        </h1>
        <p className="page-sub">
          An operator asks for help from the dispatch dashboard; accepting opens their screen here and
          lets you draw on it.
        </p>
      </div>

      {/* The permission prompt is asked for HERE, from a button, and never on
          load. A notification prompt that appears before anyone has expressed
          interest is the one people deny permanently — and a denied permission
          cannot be asked for a second time. */}
      {notifyState === 'default' && (
        <p className="assist-alerts">
          <Bell size={14} />
          Requests arrive while nobody is on this screen.
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void requestAssistNotifications().then(setNotifyState)}
          >
            Notify me
          </button>
        </p>
      )}
      {notifyState === 'denied' && (
        <p className="assist-alerts">
          <Bell size={14} />
          Desktop alerts are blocked for this site — the tab title and the badge beside “Assist” still
          count waiting requests.
        </p>
      )}

      {error && <p className="note note-danger">Could not read the queue: {error.message}</p>}
      {actionError && <p className="note note-danger">{actionError}</p>}
      {shared?.error && <p className="note note-danger">Ending the call: {shared.error}</p>}

      {data && !data.configured && (
        <p className="note note-warn">
          Screen sharing is not configured — <code>DAILY_API_KEY</code> is unset on this deployment, so
          the “Ask for help” button in the dashboard answers 503. Nothing below can be accepted.
        </p>
      )}

      {loading && !data && <p className="note">Loading…</p>}

      {data && data.open.length === 0 && (
        <div className="card card-pad assist-empty">
          <MonitorSmartphone size={22} />
          <p>Nobody is waiting.</p>
        </div>
      )}

      {data?.open.map((session) => (
        <div key={session.id} className="card card-pad assist-card">
          <div className="assist-card-head">
            <span className="badge badge-accent">{session.tenantKey}</span>
            <span className={`badge ${STATUS_TONE[session.status] ?? ''}`}>{session.status}</span>
            <span className="assist-muted">{age(session.createdAt)}</span>
            <span className="wt-spacer" />
            {session.status === 'pending' ? (
              canRespond && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy === session.id || !data.configured}
                onClick={() => void accept(session)}
              >
                {busy === session.id ? 'Opening a room…' : 'Accept'}
              </button>
              )
            ) : canRespond && mine(session) ? (
              // Accepted by this person (or they are root): back in, or end it
              // -- the two things a session left open by a closed tab needs.
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy === session.id}
                  onClick={() => void rejoin(session)}
                >
                  {busy === session.id ? 'Opening…' : 'Rejoin'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy === session.id}
                  onClick={() => void end(session)}
                >
                  End
                </button>
              </>
            ) : (
              <span className="assist-muted">
                {session.status === 'live' ? 'In progress' : 'Waiting for them to join'}
                {session.acceptedBy ? ` · ${session.acceptedBy}` : ''}
              </span>
            )}
          </div>

          <p className="assist-who">
            {session.requestedByName || session.requestedByEmail || session.requestedBy}
            {session.screen && <span className="assist-muted"> · on {session.screen}</span>}
          </p>
          {session.note && <p className="assist-note">“{session.note}”</p>}
          <ContextRow session={session} />
        </div>
      ))}

      {data && data.recent.length > 0 && (
        <>
          <h2 className="assist-subhead">Recent</h2>
          <div className="card wt-table-wrap">
            <table className="assist-table wt-stack">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Who</th>
                  <th>Status</th>
                  <th>Handled by</th>
                  <th>Asked</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((session) => (
                  <tr key={session.id}>
                    <td data-label="Tenant">{session.tenantKey}</td>
                    <td data-label="Who">{session.requestedByName || session.requestedByEmail || '—'}</td>
                    <td data-label="Status">
                      <span className={`badge ${STATUS_TONE[session.status] ?? ''}`}>{session.status}</span>
                    </td>
                    <td className="wt-stack-wide" data-label="Handled by">{session.acceptedBy || '—'}</td>
                    <td className="assist-muted" data-label="Asked">
                      {age(session.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
